/** Transport-neutral history and retry helpers for both chat surfaces. */
export interface HistoryMessage {
  id: number;
  role: string;
  created_at: string;
  content_blocks: Array<Record<string, unknown>>;
}

export interface RetryAttachment {
  kind: "image" | "text" | "media";
  image_url?: string;
  text?: string;
  label?: string | null;
  media_url?: string | null;
  media_mime?: string | null;
  media_sha256?: string | null;
}

export function mergeMessagePages<T extends HistoryMessage>(pages: Array<{ items: T[] }>): T[] {
  const byId = new Map<number, T>();
  for (const page of [...pages].reverse()) {
    for (const message of page.items) byId.set(message.id, message);
  }
  return [...byId.values()].sort((a, b) =>
    Date.parse(a.created_at) - Date.parse(b.created_at) || a.id - b.id,
  );
}

/** Repeated attempts can leave several assistant rows after one question.
 * Retry always targets the last user turn, including when its first attempt
 * failed before saving any assistant row. Original media is retained. */
export function retryPrompt<T extends HistoryMessage>(messages: T[]): {
  prompt: T;
  history: T[];
  attachments: RetryAttachment[];
} | null {
  let index = messages.length - 1;
  while (index >= 0 && messages[index]?.role !== "user") index -= 1;
  const prompt = messages[index];
  if (!prompt) return null;
  const attachments: RetryAttachment[] = [];
  for (const block of prompt.content_blocks) {
    const label = typeof block.label === "string" ? block.label : null;
    if (block.type === "image_url") {
      const image = block.image_url as { url?: unknown } | undefined;
      if (typeof image?.url === "string") attachments.push({ kind: "image", image_url: image.url, label });
    } else if (block.type === "text_attachment" && typeof block.text === "string") {
      attachments.push({ kind: "text", text: block.text, label });
    } else if (block.type === "media") {
      attachments.push({
        kind: "media", label,
        media_url: typeof block.media_url === "string" ? block.media_url : null,
        media_mime: typeof block.media_mime === "string" ? block.media_mime : null,
        media_sha256: typeof block.media_sha256 === "string" ? block.media_sha256 : null,
      });
    }
  }
  return { prompt, history: messages.slice(0, index), attachments };
}
